import React, { useMemo } from "react";
import { Link } from "react-router-dom";
import { daysOverdue, todayIso } from "@/lib/collection";

const dayBg = (isToday, sun) => (isToday ? "bg-amber-500/10" : sun ? "bg-slate-900/60" : "");
const loadTone = (over, fullDay) => (over ? "bg-rose-600 text-white" : fullDay ? "bg-amber-500/30 text-amber-100" : "text-slate-600");
const BAR_TONE = { late: "bg-rose-600/80 border-rose-400", due: "bg-sky-600/70 border-sky-400", none: "bg-slate-700/70 border-slate-500 border-dashed" };
const barState = (late, e) => (late > 0 ? "late" : e ? "due" : "none");
const barLabel = (late, e) => {
  if (late > 0) return `overdue ${late}d`;
  if (e) return `due ${new Date(`${e}T00:00:00`).toLocaleDateString("en-SG", { day: "2-digit", month: "short" })}`;
  return "set due date";
};

const DAY = 86400000;
const iso = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
const addDays = (isoDate, n) => { const d = new Date(`${isoDate}T00:00:00`); d.setDate(d.getDate() + n); return iso(d); };
const diffDays = (a, b) => Math.round((new Date(`${b}T00:00:00`) - new Date(`${a}T00:00:00`)) / DAY);
const startOf = (q) => (q.status_changed_at || q.created_at).slice(0, 10);

// Week-style timeline: one row per vehicle, bar from approval to expected completion; header shows bay load per day
export default function WorkshopTimeline({ items, bays, onDue }) {
  const today = todayIso();
  const { from, days } = useMemo(() => {
    const starts = items.map(startOf);
    const ends = items.map((q) => q.expected_done || today);
    const from = [...starts, addDays(today, -3)].sort()[0];
    const to = [...ends, addDays(today, 7)].sort().slice(-1)[0];
    const n = Math.min(Math.max(diffDays(from, to) + 2, 10), 45);
    return { from, days: Array.from({ length: n }, (_, i) => addDays(from, i)) };
  }, [items, today]);

  const load = useMemo(() => days.map((d) => items.filter((q) => startOf(q) <= d && (q.expected_done || today) >= d).length), [days, items, today]);
  const col = (d) => diffDays(from, d) + 1;
  const gridCols = { gridTemplateColumns: `180px repeat(${days.length}, minmax(34px, 1fr))` };

  return (
    <div className="rounded-2xl border border-slate-800 bg-[#151D2E] overflow-x-auto" data-testid="workshop-timeline">
      <div className="min-w-[900px]">
        <div className="grid border-b border-slate-800 sticky top-0 bg-[#151D2E]" style={gridCols}>
          <div className="px-3 py-2 text-[10px] uppercase tracking-wider text-slate-500 font-semibold">Vehicle</div>
          {days.map((d, i) => {
            const dt = new Date(`${d}T00:00:00`);
            const sun = dt.getDay() === 0; const isToday = d === today;
            const over = bays > 0 && load[i] > bays; const fullDay = bays > 0 && load[i] === bays;
            return (
              <div key={d} className={`px-1 py-1.5 text-center border-l border-slate-800/60 ${dayBg(isToday, sun)}`} data-testid={`timeline-day-${d}`}>
                <div className={`text-[10px] ${isToday ? "text-amber-300 font-bold" : "text-slate-500"}`}>{dt.toLocaleDateString("en-SG", { weekday: "short" }).slice(0, 2)}</div>
                <div className={`text-xs font-mono-num ${isToday ? "text-amber-200 font-bold" : "text-slate-300"}`}>{dt.getDate()}</div>
                <div className={`mt-0.5 text-[10px] font-mono-num rounded ${loadTone(over, fullDay)}`} title={bays ? `${load[i]} of ${bays} bays` : `${load[i]} in shop`}>{load[i] || ""}</div>
              </div>
            );
          })}
        </div>
        {items.map((q) => {
          const s = startOf(q); const e = q.expected_done; const late = daysOverdue(e);
          const startCol = Math.max(col(s), 1); const endCol = Math.min(col(e || today) + 1, days.length + 1);
          const tone = BAR_TONE[barState(late, e)];
          return (
            <div key={q.id} className="grid border-b border-slate-800/60 hover:bg-slate-900/30" style={gridCols} data-testid={`timeline-row-${q.id}`}>
              <div className="px-3 py-2 min-w-0">
                <Link to={`/quotes?q=${encodeURIComponent(q.quote_no)}`} className="font-mono-num font-bold text-sm text-slate-100 hover:text-amber-300 block truncate">{q.plate || "—"}</Link>
                <div className="text-[11px] text-slate-400 truncate">{q.customer_name || "Walk-in"}{q.vehicle ? ` · ${q.vehicle}` : ""}</div>
              </div>
              <div className="relative py-2" style={{ gridColumn: `2 / span ${days.length}` }}>
                <div className="absolute inset-0 grid" style={{ gridTemplateColumns: `repeat(${days.length}, minmax(34px, 1fr))` }}>
                  {days.map((d) => <div key={d} className={`border-l border-slate-800/40 ${d === today ? "bg-amber-500/5" : ""}`} />)}
                </div>
                <div className="relative grid h-7" style={{ gridTemplateColumns: `repeat(${days.length}, minmax(34px, 1fr))` }}>
                  <label className={`h-7 rounded-md border px-2 flex items-center gap-1 text-[11px] font-semibold text-white truncate cursor-pointer ${tone}`} style={{ gridColumn: `${startCol} / ${endCol}` }} title={e ? `Approved ${s} → due ${e} (click to change)` : "No due date — click to set"} data-testid={`timeline-bar-${q.id}`}>
                    <span className="truncate">{barLabel(late, e)}</span>
                    <input type="date" value={e || ""} min={today} onChange={(ev) => onDue(q, ev.target.value || null)} className="absolute w-0 h-0 opacity-0" data-testid={`timeline-due-input-${q.id}`} />
                  </label>
                </div>
              </div>
            </div>
          );
        })}
        <div className="px-3 py-2 text-[11px] text-slate-500 flex gap-4 flex-wrap">
          <span><span className="inline-block w-3 h-2 rounded bg-sky-600/70 mr-1" />on schedule</span>
          <span><span className="inline-block w-3 h-2 rounded bg-rose-600/80 mr-1" />overdue</span>
          <span><span className="inline-block w-3 h-2 rounded bg-slate-700 border border-dashed border-slate-500 mr-1" />no due date</span>
          {bays > 0 && <span>day counter turns <span className="text-amber-200">amber</span> at {bays} bays, <span className="text-rose-300">red</span> when over</span>}
        </div>
      </div>
    </div>
  );
}
